/**
 * Lógica de “¿Hay espacio?” sobre una foto de la agenda.
 *
 * En producción, el paso 1 debe usar POST /jobs/smart-scheduling (backend) y esto queda
 * como respaldo / vista previa. Los pasos 2 (reajustar) y 3 (otras fechas) aún no tienen
 * endpoint: ver HANDOFF.md § "Lo que requiere API".
 *
 * Funciones puras: reciben `Snapshot` y no tocan estado global, así se pueden probar.
 */
import type {
  Block, Client, DateKey, ExpectedVisitState, GeoPoint, Hour, RescheduleRequest,
  SmartSuggestion, SpaceRequest, Team, Visit,
} from '../data/types';
import { addDays, ceilHalfHour, miles } from './time';

export interface Snapshot {
  today: DateKey;
  now: Hour;
  teams: Team[];
  visits: Visit[];
  blocks: Block[];
  clients: Client[];
  closedDays: DateKey[];
}

export interface Interval { start: Hour; end: Hour }

const MIN_GAP = 0.25;

function busy(s: Snapshot, teamId: string, date: DateKey, ignoreVisitUid?: string): Interval[] {
  const v = s.visits
    .filter(x => x.teamId === teamId && x.date === date && x.status !== 'cancelled' && x.uid !== ignoreVisitUid)
    .map(x => ({ start: x.start, end: x.end }));
  const b = s.blocks.filter(x => x.teamId === teamId && x.date === date).map(x => ({ start: x.start, end: x.end }));
  return [...v, ...b].sort((a, c) => a.start - c.start);
}

/** Huecos libres del equipo ese día (desde “ahora” si es hoy). */
export function freeGaps(s: Snapshot, teamId: string, date: DateKey, ignoreVisitUid?: string): Interval[] {
  if (s.closedDays.includes(date)) return [];
  const team = s.teams.find(t => t.id === teamId);
  if (!team) return [];
  let cursor = date === s.today ? Math.max(team.workStart, s.now) : team.workStart;
  const out: Interval[] = [];
  for (const b of busy(s, teamId, date, ignoreVisitUid)) {
    if (b.end <= cursor) continue;
    if (b.start - cursor >= MIN_GAP) out.push({ start: cursor, end: Math.min(b.start, team.workEnd) });
    cursor = Math.max(cursor, b.end);
  }
  if (team.workEnd - cursor >= MIN_GAP) out.push({ start: cursor, end: team.workEnd });
  return out;
}

/** Horas libres totales (heatmap “¿Dónde hay espacio esta semana?”). */
export function freeHours(s: Snapshot, teamId: string, date: DateKey): number {
  return freeGaps(s, teamId, date).reduce((acc, g) => acc + (g.end - g.start), 0);
}

/** Ocupación del día: horas reservadas / horas laborables (sin descansos). */
export function occupancy(s: Snapshot, teamId: string, date: DateKey): number {
  const team = s.teams.find(t => t.id === teamId);
  if (!team) return 0;
  const blocks = s.blocks.filter(b => b.teamId === teamId && b.date === date).reduce((a, b) => a + b.end - b.start, 0);
  const capacity = team.workEnd - team.workStart - blocks;
  const booked = s.visits.filter(v => v.teamId === teamId && v.date === date && v.status !== 'cancelled')
    .reduce((a, v) => a + v.end - v.start, 0);
  return capacity > 0 ? Math.min(1, booked / capacity) : 1;
}

function locationBefore(s: Snapshot, team: Team, date: DateKey, at: Hour): GeoPoint | null {
  const prev = s.visits
    .filter(v => v.teamId === team.id && v.date === date && v.end <= at && v.status !== 'cancelled')
    .sort((a, b) => b.end - a.end)[0];
  if (!prev) return team.base;
  return s.clients.find(c => c.uid === prev.clientUid)?.location ?? null;
}

function suggestionFor(s: Snapshot, team: Team, date: DateKey, gap: Interval, req: SpaceRequest): SmartSuggestion | null {
  const earliest = date === s.today ? ceilHalfHour(s.now + 0.25) : gap.start;
  const start = Math.max(ceilHalfHour(gap.start), earliest);
  const end = start + req.durationH;
  if (end > gap.end + 1e-9) return null;
  const from = locationBefore(s, team, date, start);
  const dist = from ? miles(from, req.location) : 0;
  const reasons = ['Sin choques de horario'];
  if (from) reasons.push(`A ${dist} mi de su trabajo anterior`);
  const fit = gap.end - gap.start - req.durationH;
  if (fit < 0.75) reasons.push('Aprovecha el hueco completo');
  const score = Math.max(0, Math.round(100 - dist * 6 - (start - earliest) * 2 - (fit > 3 ? 3 : 0)));
  return {
    teamId: team.id, date, start, end, score, matchReasons: reasons, travelDistanceMiles: dist,
    routeReadiness: from ? 'ready' : 'missing_locations',
  };
}

/** Paso 1: espacios directos ese día (máx. uno por equipo, ordenados por puntaje). */
export function findSlots(s: Snapshot, req: SpaceRequest, date: DateKey): SmartSuggestion[] {
  const out: SmartSuggestion[] = [];
  for (const team of s.teams) {
    for (const gap of freeGaps(s, team.id, date)) {
      const sug = suggestionFor(s, team, date, gap, req);
      if (sug) { out.push(sug); break; }
    }
  }
  return out.sort((a, b) => b.score - a.score || a.start - b.start);
}

export interface MovePlan {
  /** Trabajo que se mueve (cliente flexible). */
  moved: Visit;
  movedTo: { teamId: string; start: Hour; end: Hour };
  /** Lugar que se libera para el cliente nuevo. */
  slot: SmartSuggestion;
  shiftHours: number;
  request: RescheduleRequest;
}

/** Paso 2: mover UN trabajo de un cliente flexible para abrir espacio. */
export function proposeMove(s: Snapshot, req: SpaceRequest, date: DateKey): MovePlan | null {
  const candidates = s.visits.filter(v =>
    v.date === date && v.status === 'scheduled' && (date !== s.today || v.start > s.now) &&
    s.clients.find(c => c.uid === v.clientUid)?.flexible);
  const plans: MovePlan[] = [];
  for (const v of candidates) {
    const team = s.teams.find(t => t.id === v.teamId)!;
    // ¿Cabe el nuevo cliente si quitamos este trabajo?
    let slot: SmartSuggestion | null = null;
    for (const gap of freeGaps(s, team.id, date, v.uid)) {
      slot = suggestionFor(s, team, date, gap, req);
      if (slot) break;
    }
    if (!slot) continue;
    const taken: Interval = { start: slot.start, end: slot.end };
    const dur = v.end - v.start;
    // ¿A dónde va el trabajo movido? Otro equipo, o el mismo equipo más tarde.
    for (const other of s.teams) {
      const gaps = freeGaps(s, other.id, date, other.id === team.id ? v.uid : undefined)
        .flatMap(g => (other.id === team.id ? subtract(g, taken) : [g]));
      for (const g of gaps) {
        const start = Math.max(ceilHalfHour(g.start), date === s.today ? ceilHalfHour(s.now + 0.25) : g.start);
        if (start + dur > g.end + 1e-9) continue;
        const shift = Math.abs(start - v.start);
        if (shift > 3) continue;
        plans.push({
          moved: v, movedTo: { teamId: other.id, start, end: start + dur }, slot, shiftHours: shift,
          request: buildReschedule(v, { teamId: other.id, date, start, end: start + dur }),
        });
        break;
      }
    }
  }
  plans.sort((a, b) => a.shiftHours - b.shiftHours || b.slot.score - a.slot.score);
  return plans[0] ?? null;
}

function subtract(g: Interval, cut: Interval): Interval[] {
  const out: Interval[] = [];
  if (cut.start - g.start >= MIN_GAP) out.push({ start: g.start, end: Math.min(cut.start, g.end) });
  if (g.end - cut.end >= MIN_GAP) out.push({ start: Math.max(cut.end, g.start), end: g.end });
  return out.filter(x => x.end > x.start);
}

/** Paso 3: las siguientes opciones en los próximos días (máx. una por día). */
export function nextDays(s: Snapshot, req: SpaceRequest, fromOffset = 1, days = 7, limit = 3): SmartSuggestion[] {
  const out: SmartSuggestion[] = [];
  for (let i = fromOffset; i <= days && out.length < limit; i++) {
    const date = addDays(s.today, i);
    const best = findSlots(s, req, date)[0];
    if (best) out.push(best);
  }
  return out;
}

/** Disponibilidad de la semana para el mini calendario del paso 3. */
export function weekAvailability(s: Snapshot, req: SpaceRequest, days = 7): { date: DateKey; count: number }[] {
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(s.today, i);
    return { date, count: findSlots(s, req, date).length };
  });
}

export function expectedState(v: Visit): ExpectedVisitState {
  return {
    status: v.status, date: v.date, start: v.start, end: v.end, teamId: v.teamId,
    assignments: v.assignments.map(a => ({ memberUid: a.memberUid, status: a.status })),
    updatedAt: v.updatedAt,
  };
}

export function buildReschedule(v: Visit, to: RescheduleRequest['to']): RescheduleRequest {
  return { visitUid: v.uid, to, expectedVisitState: expectedState(v) };
}

export type SpaceResult =
  | { step: 1; options: SmartSuggestion[] }
  | { step: 2; plan: MovePlan }
  | { step: 3; options: SmartSuggestion[] };

/** Orden del flujo de llamada: directo → reajustar → otras fechas. */
export function evaluate(s: Snapshot, req: SpaceRequest): SpaceResult {
  const firstDay = req.when === 'manana' ? addDays(s.today, 1) : s.today;
  const direct = req.when === 'semana'
    ? nextDays(s, req, 0, 7, 3)
    : findSlots(s, req, firstDay).slice(0, 3);
  if (direct.length) return { step: 1, options: direct };
  const plan = proposeMove(s, req, firstDay);
  if (plan) return { step: 2, plan };
  return { step: 3, options: nextDays(s, req, req.when === 'manana' ? 2 : 1) };
}
